import { array, object, string } from 'yup';
import { treatmentRecordCommonSchema } from './treatmentRecordCommon';
import { getNote } from '../helper-functions/getNote';
import { NOTE_TYPE } from '../../constants/appConstants';
import { nullToEmptyString, nullToArray } from './transforms';

export const laserTreatmentRecordSchema = object().shape({
  treatmentAttributes: object().shape({
    skintelMiReading: string().required('Skintel MI Reading').nullable(true),
    maskBaseProduct: array(string())
      .when(['maskEnhancements'], {
        is: (enhancement: string[] | undefined) =>
          (enhancement?.length === 0 || !enhancement),
        then: array(string())
          .required('Mask Base Product')
          .min(1, 'Mask Base Product'),
      })
      .nullable()
      .transform(nullToArray),

    laserFacial1064Wavelength: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('laserFacial1064'),
        then: string().required('Laser Facial Wavelength'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    laserFacial1064SpotSize: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('laserFacial1064'),
        then: string().required('Laser Facial Spot Size'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    laserFacial1064Fluence: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('laserFacial1064'),
        then: string().required('Laser Facial Fluence'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    laserFacial1064PulseWidth: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('laserFacial1064'),
        then: string().required('Laser Facial Pulse Width'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    laserFacial1064RepRate: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('laserFacial1064'),
        then: string().required('Laser Facial Rep Rate'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    laserFacial1064PulseCount: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('laserFacial1064'),
        then: string().required('Laser Facial Pulse Count'),
      })
      .nullable(true)
      .transform(nullToEmptyString),

    benignLesionsWavelength: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('benignPigmentedLesions'),
        then: string().required('Benign Pigmented Lesions Wavelength'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    benignLesionsSpotSizeAndPulseWidth: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('benignPigmentedLesions'),
        then: string().required(
          'Benign Pigmented Lesions Spot size and Pulse Width'
        ),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    benignLesionsFluence: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('benignPigmentedLesions'),
        then: string().required('Benign Pigmented Lesions Fluence'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    benignLesionsRepRate: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('benignPigmentedLesions'),
        then: string().required('Benign Pigmented Lesions Rep Rate'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    benignLesionsPulseCount: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('benignPigmentedLesions'),
        then: string().required('Benign Pigmented Lesions Pulse Count'),
      })
      .nullable(true)
      .transform(nullToEmptyString),

    vascularLesionsWavelength: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('vascularLesions'),
        then: string().required('Vascular Lesions Wavelength'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    vascularLesionsSpotSize: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('vascularLesions'),
        then: string().required('Vascular Lesions Spot Size'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    vascularLesionsFluence: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('vascularLesions'),
        then: string().required('Vascular Lesions Fluence'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    vascularLesionsPulseWidth: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('vascularLesions'),
        then: string().required('Vascular Lesions Pulse Width'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    vascularLesionsRepRate: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('vascularLesions'),
        then: string().required('Vascular Lesions Rep Rate'),
      })
      .nullable(true)
      .transform(nullToEmptyString),
    vascularLesionsPulseCount: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('vascularLesions'),
        then: string().required('Vascular Lesions Pulse Count'),
      })
      .nullable(true)
      .transform(nullToEmptyString),

    laserMask: string()
      .when('laserTreatmentType', {
        is: (laserTreatmentType: string[] | undefined) =>
          laserTreatmentType?.includes('sheetMask'),
        then: string(),
      })
      .nullable(true)
      .transform(nullToEmptyString),

    moisturizerAndSpf: array(string())
      .required('Moisturizer & SPF')
      .min(1, 'Moisturizer & SPF')
      .nullable(true)
      .transform(nullToArray),

    ...treatmentRecordCommonSchema.treatmentAttributes,
  }),
  ...treatmentRecordCommonSchema.topLevel,
  note: treatmentRecordCommonSchema.topLevel.note.test(
    'internalNotes',
    'Internal Notes',
    (value) => {
      const treatmentNoteText =
        getNote(NOTE_TYPE.generalNotes, value)?.text?.trim() ?? '';
      return !!treatmentNoteText;
    }
  ),
});
